import { useEffect, useRef, useState } from "react";
import { StreamVisibilityManager } from "@/lib/streamVisibilityManager";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";

const POLL_INTERVAL_MS = 10_000;
const MAX_POLL_THREADS = 5;

/**
 * Polls the backend for threads that have active SSE queues (i.e., a response
 * is still being generated server-side). Used to show a subtle activity
 * indicator in the thread list for streams that were disconnected because the
 * user navigated away.
 *
 * Returns a Set of thread ids that currently have an active backend queue.
 */
export function useBackgroundStreamingPoll(): Set<string> {
  const [activeThreadIds, setActiveThreadIds] = useState<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    async function poll() {
      const candidates = StreamVisibilityManager.getRecentlyActiveThreadIds()
        .slice(0, MAX_POLL_THREADS);

      if (candidates.length === 0) {
        setActiveThreadIds((prev) => (prev.size === 0 ? prev : new Set()));
        return;
      }

      const stillActive = new Set<string>();

      // Check each candidate in parallel
      const results = await Promise.allSettled(
        candidates.map(async (threadId) => {
          try {
            const res = await apiAuthFetch(`api/threads/${threadId}/streaming`, {
              method: "GET",
            });
            if (res.ok) {
              const data = await res.json();
              return { threadId, active: data.active === true };
            }
            ClientLogger.warn("Background stream poll failed", { threadId, status: res.status });
            return { threadId, active: false };
          } catch (err) {
            // Network error — can't determine state. Return undefined so the thread
            // stays in recentlyActive; the 5-min TTL or next successful poll handles cleanup.
            ClientLogger.warn("Background stream poll error", { threadId, error: err });
            return { threadId, active: undefined };
          }
        }),
      );

      for (const result of results) {
        if (result.status === "fulfilled") {
          const { active, threadId } = result.value;
          if (active === true) {
            stillActive.add(threadId);
          } else if (active === false) {
            // Definitive server response: thread finished — remove from recently-active
            StreamVisibilityManager.removeFromRecentlyActive(threadId);
          }
          // active === undefined (network error): leave in recentlyActive, don't add to stillActive
        }
      }

      setActiveThreadIds((prev) => {
        // Avoid unnecessary re-renders if the set hasn't changed
        if (prev.size === stillActive.size && [...prev].every((id) => stillActive.has(id))) {
          return prev;
        }
        return stillActive;
      });
    }

    // Initial poll
    poll().catch((err) => {
      ClientLogger.debug("Background stream poll error", { error: err });
    });

    // Periodic poll
    intervalRef.current = setInterval(() => {
      poll().catch((err) => {
        ClientLogger.debug("Background stream poll error", { error: err });
      });
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return activeThreadIds;
}
